import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AllergenType, ItemContext_SaltItemFieldsFragment } from '@/types';
import { ContainmentLevel } from '@/contexts/item-context-types';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { RichInfoSection } from './rich-info-section';
import { Text, View } from 'react-native';

interface Props {
  horizontal?: boolean;
  saltItemInfo: ItemContext_SaltItemFieldsFragment;
}

interface AllergensProps {
  allergens: AllergenType[];
}

/* eslint-disable typescript-sort-keys/string-enum */
enum AllergenCode {
  SA = 'almonds',
  GB = 'barley',
  SR = 'brazil_nuts',
  SC = 'cashews',
  AW = 'cereals',
  NM = 'corn',
  AC = 'crustaceans',
  AE = 'eggs',
  AF = 'fish',
  AX = 'gluten',
  SH = 'hazelnuts',
  GK = 'kamut',
  ML = 'lactose',
  SM = 'macadamia_nuts',
  AM = 'milk',
  UM = 'molluscs',
  BM = 'mustard',
  GO = 'oats',
  AP = 'peanuts',
  SP = 'pecan_nuts',
  ST = 'pistachio',
  QN = 'queensland_nuts',
  AS = 'sesame',
  UN = 'shellfish',
  AY = 'soy',
  GS = 'spelt',
  AU = 'sulphites',
  AN = 'tree_nuts',
  SW = 'walnuts',
  UW = 'wheat',
}
/* eslint-enable typescript-sort-keys/string-enum */

const getAllergenCodeFromName = (name: string): string | undefined => {
  return AllergenCode[name as keyof typeof AllergenCode];
};

const hasValidAllergenCode = (allergen: AllergenType): boolean => {
  return Boolean(allergen.name && getAllergenCodeFromName(allergen.name));
};

const Allergens: React.FC<AllergensProps> = ({ allergens }) => {
  const [styles] = useStyles();

  return (
    <View style={styles.base}>
      {allergens.map(({ name }) => {
        const allergenCode = getAllergenCodeFromName(name ?? '');

        return (
          <View key={name} style={styles.allergenItem}>
            <Text style={[TYPOGRAPHY.xsmall, FONTS.primarySemiBold]}>
              {i18n.t(`allergen_code.${allergenCode?.toLowerCase()}`)}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

export const RichInfoSaltAllergens: React.FC<Props> = ({
  saltItemInfo,
  horizontal,
}) => {
  const [freeFromAllergens, containsAllergens, mayContainAllergens] = useMemo<
    [AllergenType[], AllergenType[], AllergenType[]]
  >(() => {
    const freeFrom: AllergenType[] = [];
    const contains: AllergenType[] = [];
    const mayContain: AllergenType[] = [];
    const allAllergens = saltItemInfo?.dietaryInformation.allergens || [];
    for (const allergen of allAllergens) {
      if (!hasValidAllergenCode(allergen)) {
        continue;
      }
      if (allergen.containmentLevel === ContainmentLevel.Contains) {
        contains.push(allergen);
      } else if (allergen.containmentLevel === ContainmentLevel.FreeFrom) {
        freeFrom.push(allergen);
      } else if (allergen.containmentLevel === ContainmentLevel.MayContain) {
        mayContain.push(allergen);
      }
    }

    return [freeFrom, contains, mayContain];
  }, [saltItemInfo]);

  return (
    <>
      {freeFromAllergens.length > 0 ? (
        <RichInfoSection horizontal={horizontal} title={i18n.t('free_from')}>
          <Allergens allergens={freeFromAllergens} />
        </RichInfoSection>
      ) : null}
      {containsAllergens.length > 0 ? (
        <RichInfoSection horizontal={horizontal} title={i18n.t('contains')}>
          <Allergens allergens={containsAllergens} />
        </RichInfoSection>
      ) : null}
      {mayContainAllergens.length > 0 ? (
        <RichInfoSection horizontal={horizontal} title={i18n.t('may_contain')}>
          <Allergens allergens={mayContainAllergens} />
        </RichInfoSection>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  allergenItem: {
    backgroundColor: theme.colors.allergen,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  base: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
}));
